/**
 * Global audio cache for music files
 * Reuses Audio objects to prevent redundant loading and memory waste
 */

const audioCache = new Map();
const preloadPromises = new Map();

import { resolvePublicAssetUrl } from '../utils/resolvePublicAssetUrl';

/**
 * @param {string} path - Root-relative public asset path
 * @returns {string}
 */
export function resolveAssetPath(path) {
  if (!path) {
    throw new Error('resolveAssetPath: path is required');
  }
  if (!path.startsWith('/')) {
    return path;
  }
  return resolvePublicAssetUrl(path);
}

/**
 * Get or create an Audio object for a track
 * @param {string} trackPath - Path to the music file
 * @returns {HTMLAudioElement} Audio element
 */
export function getCachedAudio(trackPath) {
  // Resolve path for Electron compatibility
  const resolvedPath = resolveAssetPath(trackPath);
  
  // Use original path as cache key for consistency
  if (audioCache.has(trackPath)) {
    const cached = audioCache.get(trackPath);
    // Clone the audio element to allow multiple simultaneous uses if needed
    // But for our use case, we'll reuse the same instance
    return cached;
  }

  const audio = new Audio(resolvedPath);
  audio.loop = true;
  audio.preload = 'auto'; // Preload metadata and some data
  
  audioCache.set(trackPath, audio);
  return audio;
}

/**
 * Preload a music track in the background
 * @param {string} trackPath - Path to the music file
 * @returns {Promise<void>} Promise that resolves when preload is complete
 */
export function preloadTrack(trackPath) {
  if (preloadPromises.has(trackPath)) {
    return preloadPromises.get(trackPath);
  }

  const promise = new Promise((resolve, reject) => {
    const audio = getCachedAudio(trackPath);

    // If already loaded, resolve immediately
    if (audio.readyState >= 2) {
      // HAVE_CURRENT_DATA
      resolve();
      return;
    }

    // Never call load() while playback is active — same cached element is shared with useMusic;
    // load() aborts decoding and causes choppy audio on Android WebView.
    if (!audio.paused) {
      resolve();
      return;
    }

    const handleCanPlay = () => {
      audio.removeEventListener('canplaythrough', handleCanPlay);
      audio.removeEventListener('error', handleError);
      resolve();
    };

    const handleError = (error) => {
      audio.removeEventListener('canplaythrough', handleCanPlay);
      audio.removeEventListener('error', handleError);
      console.warn(`Failed to preload track: ${trackPath}`, error);
      reject(error);
    };

    audio.addEventListener('canplaythrough', handleCanPlay, { once: true });
    audio.addEventListener('error', handleError, { once: true });

    // new Audio(path) already sets src; without load() many browsers defer fetch until play().
    audio.load();
  });

  preloadPromises.set(trackPath, promise);
  return promise;
}

/**
 * Preload multiple tracks in parallel
 * @param {string[]} trackPaths - Array of track paths
 * @returns {Promise<void>} Promise that resolves when all tracks are preloaded
 */
export function preloadTracks(trackPaths) {
  return Promise.allSettled(
    trackPaths.map(track => preloadTrack(track))
  );
}

/**
 * Clear audio cache (useful for memory management)
 * @param {string} trackPath - Optional: clear specific track, or all if not provided
 */
export function clearAudioCache(trackPath = null) {
  if (trackPath) {
    const audio = audioCache.get(trackPath);
    if (audio) {
      audio.pause();
      audio.src = '';
      audioCache.delete(trackPath);
      preloadPromises.delete(trackPath);
    }
  } else {
    // Clear all
    audioCache.forEach((audio) => {
      audio.pause();
      audio.src = '';
    });
    audioCache.clear();
    preloadPromises.clear();
  }
}

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export function getCacheStats() {
  return {
    cachedTracks: audioCache.size,
    preloadingTracks: preloadPromises.size,
    tracks: Array.from(audioCache.keys())
  };
}

